if (!customElements.get('charity-donation')) {
  customElements.define(
    'charity-donation',
    class CharityDonation extends HTMLElement {
      constructor() {
        super();
        this.variantId = this.dataset.variantId;
        this.currentAmount = parseInt(this.dataset.currentAmount, 10) || 0;
        this.presetButtons = Array.from(this.querySelectorAll('.charity-donation__preset'));
        this.customInput = this.querySelector('.charity-donation__custom-input');
        this.customWrapper = this.customInput ? this.customInput.closest('.charity-donation__custom') : null;

        this.presetButtons.forEach((button) => {
          button.addEventListener('click', this.onPresetClick.bind(this));
        });

        if (this.customInput) {
          // Typing deselects the presets immediately, even before the amount is
          // committed - matches "de-select any button options when the number
          // box is used".
          this.customInput.addEventListener('input', this.onCustomInput.bind(this));
          this.customInput.addEventListener('change', this.onCustomChange.bind(this));
          this.customInput.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.customInput.blur();
            }
          });

          this.updateHasValueState();
        }
      }

      onPresetClick(event) {
        const button = event.currentTarget;
        const alreadySelected = button.getAttribute('aria-pressed') === 'true';
        // Clicking an already-selected preset toggles it off (back to no extra
        // donation); otherwise the clicked amount becomes the total.
        const amount = alreadySelected ? 0 : parseInt(button.dataset.amount, 10);

        this.clearCustomInput();
        this.setAmount(amount);
      }

      onCustomInput() {
        this.clearPresetSelection();
        this.updateHasValueState();
      }

      onCustomChange(event) {
        const amount = Math.max(0, parseInt(event.target.value, 10) || 0);
        // Donations are whole dollars (1 unit = $1), so any cents entered
        // (e.g. "20.52") are dropped from the actual amount - but display it
        // as a proper "20.00" rather than bare "20" to match the preset amounts.
        event.target.value = amount ? amount.toFixed(2) : '';
        this.clearPresetSelection();
        this.updateHasValueState();
        this.setAmount(amount);
      }

      clearPresetSelection() {
        this.presetButtons.forEach((button) => button.setAttribute('aria-pressed', 'false'));
      }

      clearCustomInput() {
        if (this.customInput) this.customInput.value = '';
        this.updateHasValueState();
      }

      updateHasValueState() {
        if (!this.customWrapper) return;
        this.customWrapper.classList.toggle('has-value', this.customInput.value !== '');
      }

      setAmount(amount) {
        if (amount === this.currentAmount) return Promise.resolve(false);

        const isInCart = this.currentAmount > 0;
        if (amount === 0 && !isInCart) return Promise.resolve(false);

        this.toggleLoading(true);

        const config = fetchConfig('json');
        let url;
        // Requested directly in the request body (same pattern cart.js's own
        // updateQuantity uses) rather than relying on the pub/sub-triggered
        // onCartUpdate() refetch, which only swaps the items list/footer and
        // never touches the cart-icon-bubble section - so the header count
        // was going stale after a donation.
        const sections = ['cart-drawer', 'cart-icon-bubble'];

        if (isInCart) {
          url = routes.cart_change_url;
          config.body = JSON.stringify({
            id: this.variantId,
            quantity: amount,
            sections,
            sections_url: window.location.pathname,
          });
        } else {
          url = routes.cart_add_url;
          config.body = JSON.stringify({
            items: [{ id: this.variantId, quantity: amount }],
            sections,
            sections_url: window.location.pathname,
          });
        }

        return fetch(url, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              console.error(response.description || response.message);
              return false;
            }
            this.currentAmount = amount;
            if (response.sections) this.renderSections(response.sections);
            // Source 'cart-items' tells CartItems' own subscriber this update
            // already rendered its DOM changes, so it doesn't also redo the
            // work via a redundant refetch (matches updateQuantity's own
            // publish call).
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: response });
            return true;
          })
          .catch((e) => {
            console.error(e);
            return false;
          })
          .finally(() => {
            this.toggleLoading(false);
          });
      }

      renderSections(sections) {
        if (sections['cart-drawer']) {
          // Replace the whole .drawer__inner - matching cart.js's own
          // updateQuantity(), which this mirrors - rather than just the items
          // list/footer: that also picks up the "(N items)" drawer heading
          // and re-renders this widget itself from fresh, server-computed
          // markup (correct aria-pressed state included), instead of trying
          // to patch either by hand from here.
          const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          const targetInner = document.querySelector('.drawer__inner');
          const sourceInner = html.querySelector('.drawer__inner');
          if (targetInner && sourceInner) targetInner.innerHTML = sourceInner.innerHTML;
        }

        if (sections['cart-icon-bubble']) {
          const cartIconBubble = document.getElementById('cart-icon-bubble');
          if (cartIconBubble) {
            const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
            const sourceSection = html.querySelector('.shopify-section');
            cartIconBubble.innerHTML = sourceSection ? sourceSection.innerHTML : sections['cart-icon-bubble'];
          }
        }
      }

      toggleLoading(isLoading) {
        this.classList.toggle('charity-donation--loading', isLoading);
        this.presetButtons.forEach((button) => (button.disabled = isLoading));
        if (this.customInput) this.customInput.disabled = isLoading;
      }
    }
  );
}
